import React, { useCallback, useId, useMemo, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { AddToHumanReviewButton } from "@/human-feedback/projects"
import { useBaselineDeviationsBySimulation } from "@/metrics/baselines"
import { MetricModal } from "@/metrics/configure"
import {
  Badge,
  Button,
  Table,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"
import { Skeleton } from "@coval/ui/skeleton"
import { Tooltip } from "@coval/ui/tooltip"
import { Copy, Plus, X } from "lucide-react"

import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { DrillDownColumnConfig } from "@/utils/csv/csv-export"

import { useDrillDownAdditionalMetrics } from "../hooks/useDrillDownAdditionalMetrics"
import {
  useUnifiedMetricDrillDownRows,
  type UseUnifiedMetricDrillDownRowsResult,
} from "../hooks/useUnifiedMetricDrillDownRows"
import {
  buildDrillDownConversationPath,
  getDrillDownConversationLabel,
} from "../utils/drillDownNavigation"
import type { ChartClickData } from "../widgets/types/chart"
import type { DrillDownRunRecord } from "../widgets/types/core"
import { DrillDownCSVExportDropdown } from "./DrillDownCSVExportDropdown"

const log = logger("UnifiedMetricDrillDownPanel")

interface UnifiedMetricDrillDownPanelProps {
  clickData: ChartClickData
  baselineId?: string
  drillDownRowsState?: UseUnifiedMetricDrillDownRowsResult
  focusedSimulationOutputId?: string | null
  highlightedSimulationOutputId?: string | null
  onClose: () => void
}

type DrillDownSortColumn = keyof DrillDownRunRecord | "deviationSigma"

interface ColumnConfig extends DrillDownColumnConfig {
  sortable: boolean
}

const DEFAULT_SORT: SortDescriptor = {
  columnId: "occurred_at",
  direction: "desc",
}

const getDrillDownRowId = (row: DrillDownRunRecord) =>
  `${row.simulation_output_id}-${row.metric_id}`

export const UnifiedMetricDrillDownPanel: React.FC<
  UnifiedMetricDrillDownPanelProps
> = ({
  clickData,
  baselineId,
  drillDownRowsState,
  focusedSimulationOutputId,
  highlightedSimulationOutputId,
}) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const slug = typeof router.query.slug === "string" ? router.query.slug : ""
  const instanceId = useId()

  const [sortDescriptor, setSortDescriptor] =
    useState<SortDescriptor>(DEFAULT_SORT)

  const internalDrillDownRowsState = useUnifiedMetricDrillDownRows({
    clickData,
    enabled: !drillDownRowsState,
  })
  const rowsState = drillDownRowsState ?? internalDrillDownRowsState
  const {
    error,
    isLoading,
    rows,
    simulationOutputIds,
    timezone,
    uniqueSimulationOutputIds,
  } = rowsState

  const {
    additionalMetricIds,
    additionalValuesMap,
    handleApplyMetrics,
    handleOpenMetricModal,
    handleRemoveMetric,
    handleToggleMetric,
    isAdditionalMetricValueLoading,
    isMetricModalOpen,
    metricDisplayNames,
    metricRecords,
    setIsMetricModalOpen,
    tempSelectedMetrics,
  } = useDrillDownAdditionalMetrics({ simulationOutputIds })

  // Fetch baseline deviations for the visible simulations
  const { data: baselineDeviations } = useBaselineDeviationsBySimulation(
    {
      baselineId: baselineId ?? "",
      simulationOutputIds: uniqueSimulationOutputIds,
    },
    {
      enabled: !!baselineId && uniqueSimulationOutputIds.length > 0,
    },
  )

  const deviationMap = useMemo(() => {
    const map = new Map<string, number>()
    if (!baselineDeviations) {
      return map
    }
    for (const d of baselineDeviations) {
      map.set(d.simulation_output_id, d.deviation_sigma)
    }
    return map
  }, [baselineDeviations])

  const formatTimestamp = useCallback(
    (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        return date.toLocaleString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: timezone,
        })
      } catch (error) {
        log.error("Error formatting timestamp", error, { timestamp })
        return timestamp
      }
    },
    [timezone],
  )

  const sortedRows = useMemo(() => {
    if (!rows) {
      return []
    }

    const column = sortDescriptor.columnId as DrillDownSortColumn
    return [...rows].sort((a: DrillDownRunRecord, b: DrillDownRunRecord) => {
      let aValue, bValue

      if (column === "occurred_at") {
        aValue = new Date(a.occurred_at).getTime()
        bValue = new Date(b.occurred_at).getTime()
      } else if (column === "created_at") {
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0
      } else if (column === "value") {
        aValue = a.value
        bValue = b.value
      } else if (column === "deviationSigma") {
        aValue = deviationMap.get(a.simulation_output_id) ?? 0
        bValue = deviationMap.get(b.simulation_output_id) ?? 0
      } else {
        aValue = a[column] ?? ""
        bValue = b[column] ?? ""
      }

      if (sortDescriptor.direction === "desc") {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    })
  }, [rows, sortDescriptor, deviationMap])

  // The Table virtualizes every run and keeps its active row scrolled into
  // view, which brings a focused or highlighted run into the viewport.
  const activeRowId = useMemo(() => {
    // A highlighted run outside these results falls back to the focused run.
    for (const simulationOutputId of [
      highlightedSimulationOutputId,
      focusedSimulationOutputId,
    ]) {
      const row = simulationOutputId
        ? sortedRows.find(
            (candidate) =>
              candidate.simulation_output_id === simulationOutputId,
          )
        : undefined
      if (row) {
        return getDrillDownRowId(row)
      }
    }
    return null
  }, [focusedSimulationOutputId, highlightedSimulationOutputId, sortedRows])

  // A header toggles between ascending and descending; the third, unsorted
  // step of the Table's cycle returns to ascending on the same column.
  const handleSortChange = useCallback((descriptor: SortDescriptor | null) => {
    setSortDescriptor(
      (previous) =>
        descriptor ?? { columnId: previous.columnId, direction: "asc" },
    )
  }, [])

  const metricColumnLabel = useMemo(() => {
    const metricName = clickData.displayLabel || "Value"
    return metricName.length > 30
      ? `${metricName.substring(0, 30)}...`
      : metricName
  }, [clickData.displayLabel])

  // Build columns including additional metrics
  const columns: ColumnConfig[] = useMemo(() => {
    const baseColumns: ColumnConfig[] = [
      {
        key: "simulation_output_id",
        label: "ID",
        sortable: false,
      },
      {
        key: "occurred_at",
        label: "Occurred At",
        sortable: true,
      },
      {
        key: "created_at",
        label: "Created At",
        sortable: true,
      },
      {
        key: "value",
        label: metricColumnLabel,
        sortable: true,
      },
    ]

    if (baselineId && deviationMap.size > 0) {
      baseColumns.push({
        key: "deviationSigma",
        label: "Deviation",
        sortable: true,
      })
    }

    // Add columns for additional metrics
    const additionalColumns: ColumnConfig[] = additionalMetricIds.map(
      (metricId) => ({
        key: `metric_${metricId}`,
        label: metricDisplayNames.get(metricId) || metricId.slice(0, 8),
        sortable: false,
        isAdditionalMetric: true,
        metricId,
      }),
    )

    return [...baseColumns, ...additionalColumns]
  }, [
    metricColumnLabel,
    additionalMetricIds,
    metricDisplayNames,
    baselineId,
    deviationMap,
  ])

  const handleCopyId = useCallback((e: React.SyntheticEvent, id: string) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(id)
  }, [])

  const handleCopyIdKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      handleCopyId(event, id)
    },
    [handleCopyId],
  )

  const getConversationHref = useCallback(
    (row: DrillDownRunRecord) =>
      queryParams.getHref(
        buildDrillDownConversationPath(row, clickData.dataSource),
        slug,
      ),
    [clickData.dataSource, queryParams, slug],
  )

  const handleOpenConversation = useCallback(
    (row: DrillDownRunRecord) => {
      queryParams.push(
        buildDrillDownConversationPath(row, clickData.dataSource),
        slug,
      )
    },
    [clickData.dataSource, queryParams, slug],
  )

  const renderSimulationIdCell = (row: DrillDownRunRecord) => (
    <div className="group/id flex items-center gap-1">
      <Tooltip content={row.simulation_output_id}>
        <Link
          href={getConversationHref(row)}
          prefetch={false}
          onClick={(e) => {
            e.stopPropagation()
            if (
              e.metaKey ||
              e.ctrlKey ||
              e.shiftKey ||
              e.altKey ||
              e.button !== 0
            ) {
              return
            }
            e.preventDefault()
            handleOpenConversation(row)
          }}
          className="relative text-coval-text cursor-pointer font-mono text-sm overflow-hidden whitespace-nowrap"
          aria-label={getDrillDownConversationLabel(row, clickData.dataSource)}
        >
          <span className="inline-block w-[6ch] overflow-hidden bg-linear-to-r from-coval-text to-transparent bg-clip-text text-transparent select-none">
            {row.simulation_output_id.slice(0, 6)}
          </span>
        </Link>
      </Tooltip>
      <Button
        icon
        variant="ghost"
        tooltip="Copy ID"
        aria-label={`Copy ID ${row.simulation_output_id}`}
        onClick={(e) => handleCopyId(e, row.simulation_output_id)}
        onKeyDown={(e) => handleCopyIdKeyDown(e, row.simulation_output_id)}
        className="size-6 opacity-0 transition-opacity group-hover/id:opacity-100 group-focus-within/id:opacity-100"
      >
        <Copy className="size-3.5" />
      </Button>
    </div>
  )

  const renderCell = (column: ColumnConfig, row: DrillDownRunRecord) => {
    switch (column.key) {
      case "simulation_output_id":
        return renderSimulationIdCell(row)
      case "occurred_at":
        return (
          <div className="text-left">{formatTimestamp(row.occurred_at)}</div>
        )
      case "created_at":
        return (
          <div className="text-left text-coval-text-muted">
            {row.created_at ? formatTimestamp(row.created_at) : "-"}
          </div>
        )
      case "value": {
        // Check if it's a numeric string or number
        const numValue =
          typeof row.value === "string" ? parseFloat(row.value) : row.value
        const displayValue = !isNaN(numValue) ? numValue.toFixed(2) : row.value
        return <div className="text-left">{displayValue}</div>
      }
      case "deviationSigma": {
        const sigma = deviationMap.get(row.simulation_output_id)
        if (sigma === undefined) {
          return <div className="text-left text-coval-text-muted">-</div>
        }
        const absVal = Math.abs(sigma)
        const badgeVariant =
          absVal >= 2 ? "danger" : absVal >= 1 ? "warning" : "default"
        return (
          <Badge variant={badgeVariant}>
            {sigma >= 0 ? "+" : ""}
            {sigma.toFixed(2)}σ
          </Badge>
        )
      }
      default:
        // Handle additional metric columns
        if (column.isAdditionalMetric && column.metricId) {
          if (isAdditionalMetricValueLoading) {
            return (
              <Skeleton
                aria-label="Loading additional metric value"
                className="h-4 w-12 rounded-md"
              />
            )
          }

          const metricValue = additionalValuesMap
            .get(row.simulation_output_id)
            ?.get(column.metricId)

          if (metricValue === undefined) {
            return <div className="text-left text-coval-text-muted">-</div>
          }

          const numVal =
            typeof metricValue === "string"
              ? parseFloat(metricValue)
              : metricValue
          const display = !isNaN(numVal) ? numVal.toFixed(2) : metricValue
          return <div className="text-left">{display}</div>
        }
        return row[column.key as keyof DrillDownRunRecord]
    }
  }

  const tableColumns: ColumnDef<DrillDownRunRecord>[] = columns.map(
    (column) => ({
      id: column.key,
      headerLabel: column.label,
      header: column.isAdditionalMetric ? (
        <div className="flex items-center gap-1">
          <span className="truncate max-w-[100px]">{column.label}</span>
          <Button
            icon
            variant="ghost"
            tooltip={`Remove ${column.label}`}
            onClick={() => handleRemoveMetric(column.metricId!)}
            className="size-6"
          >
            <X className="size-3" />
          </Button>
        </div>
      ) : (
        column.label
      ),
      sortable: column.sortable,
      cell: (row) => renderCell(column, row),
    }),
  )

  return (
    <div className="h-full flex flex-col bg-surface-default border-l border-coval-border">
      <div className="flex-1 flex flex-col min-h-0">
        {error ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-danger-text">
              <div className="text-sm font-medium">
                Failed to load run details
              </div>
              <div className="text-xs mt-1 text-coval-text-muted">
                Please try selecting a different data point
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">Loading run data...</div>
            </div>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">No runs found for this data point</div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col w-full flex-1 min-h-0">
            {/* Add Metric and Export Buttons */}
            <div className="flex justify-end gap-2 px-4 py-3 border-b border-coval-border bg-surface-default z-30 shrink-0">
              <AddToHumanReviewButton
                simulationOutputIds={uniqueSimulationOutputIds}
              />
              <DrillDownCSVExportDropdown
                rows={sortedRows}
                columns={columns}
                formatTimestamp={formatTimestamp}
                additionalValuesMap={additionalValuesMap}
                metricDisplayLabel={clickData.displayLabel}
                slug={slug}
              />
              <Button
                icon
                variant="secondary"
                tooltip="Add metric column"
                onClick={handleOpenMetricModal}
              >
                <Plus />
              </Button>
            </div>

            {/* Metric Selection Modal */}
            <MetricModal
              isOpen={isMetricModalOpen}
              onClose={() => setIsMetricModalOpen(false)}
              onApply={handleApplyMetrics}
              metrics={Object.values(metricRecords || {})}
              selectedMetrics={tempSelectedMetrics}
              onToggleMetric={handleToggleMetric}
              instanceId={instanceId}
              allowMultipleSelection
            />

            <div className="flex-1 min-h-0">
              <Table<DrillDownRunRecord>
                aria-label="Run details table"
                data={sortedRows}
                columns={tableColumns}
                getRowId={getDrillDownRowId}
                defaultSort={sortDescriptor}
                onSortChange={handleSortChange}
                sortStorageKey={null}
                onRowClick={handleOpenConversation}
                activeRowId={activeRowId}
                rowAttributes={(row) => ({
                  "aria-label": getDrillDownConversationLabel(
                    row,
                    clickData.dataSource,
                  ),
                  "data-simulation-output-id": row.simulation_output_id,
                })}
                emptyState="No runs found for the selected data point"
                edgeToEdge
                className="h-full"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
